import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useCountUp(target, active, duration = 1800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

function StatNumber({ value, active }) {
  const n = useCountUp(value, active);
  return <span>{n.toLocaleString()}</span>;
}

export default function Stats() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    axios.get(`${API}/metrics`).then((r) => setMetrics(r.data)).catch(() => {});
  }, []);

  const items = metrics
    ? [
        { label: "Threats blocked", value: metrics.threats_blocked, big: true },
        { label: "Cheats detected", value: metrics.cheats_detected, big: true },
        { label: "Requests secured", text: metrics.requests_secured },
        { label: "Uptime", text: metrics.uptime },
      ]
    : [];

  return (
    <section ref={ref} className="relative py-28 lg:py-36 bg-[#05050A]" data-testid="stats-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-16 max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Live from the sphere</span>
          <h2 className="mt-5 font-display font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-none">
            Protection in numbers
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
          {items.map((it, i) => (
            <motion.div
              key={it.label}
              initial={{ opacity: 0 }}
              animate={inView ? { opacity: 1 } : {}}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              data-testid={`stat-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="bg-[#0A0A12] p-8 lg:p-10 min-h-[180px] flex flex-col justify-between hover:bg-[#0D0D16] transition-colors duration-300"
            >
              <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{it.label}</span>
              <span className="mt-6 font-display font-700 text-2xl lg:text-3xl text-white tracking-tight glow-cyan tabular-nums break-all">
                {it.big ? <StatNumber value={it.value} active={inView} /> : it.text || "—"}
              </span>
            </motion.div>
          ))}
          {!metrics &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-[#0A0A12] p-8 min-h-[180px] animate-pulse" />
            ))}
        </div>
      </div>
    </section>
  );
}
